import React, { useRef, useEffect } from "react";
import { QuestionRenderer } from "modules/questions/QuestionRenderer";
import { convertMathJax, typesetMath } from "modules/assessment/AssessmentApp";
import { QuestionType } from "shared/config/adaptiveLearningConfig";

/**
 * QuestionDisplay Component
 * Displays a question using existing assessment components
 */
const QuestionDisplay = ({
  question,
  answer,
  onAnswerChange,
  questionNumber,
  totalQuestions,
}) => {
  const optionsRef = useRef(null);

  useEffect(() => {
    typesetMath(optionsRef.current);
  }, [question, answer]);

  if (!question) return null;

  // Normalize question structure to match assessment format
  const normalizedQuestion = {
    ...question,
    questionText: question.questionText || question.question || "",
    questionType: question.questionType || question.type || "MULTIPLE_CHOICE",
    // Ensure details array exists for QuestionRenderer (for MATCHING, QUESTION_TABLE, etc.)
    details: question.details || [],
    // Normalize answerChoices from options array if needed
    answerChoices: question.answerChoices || (question.options || []).map((opt, idx) => {
      // Handle both string options and object options
      if (typeof opt === "string") {
        return {
          id: idx + 1,
          choiceText: opt,
          isCorrect: question.correctAnswer === opt || question.correctAnswer === String(idx),
        };
      }
      // If it's already an object, ensure it has the right structure
      return {
        id: opt.id || idx + 1,
        choiceText: opt.choiceText || opt.text || opt.option || String(opt),
        isCorrect: opt.isCorrect || false,
      };
    }),
  };

  const renderAnswerChoices = () => {
    // For multiple choice questions
    if (
      question.questionType === QuestionType.MULTIPLE_CHOICE ||
      question.questionType === "MULTIPLE_CHOICE"
    ) {
      return (
        <div ref={optionsRef} className="mt-4">
          {normalizedQuestion.answerChoices
            ?.slice()
            .sort((a, b) => (a.id || 0) - (b.id || 0))
            .map((choice, index) => {
              const choiceText = choice.choiceText || choice;
              const isSelected = answer === choiceText;
              const letter = String.fromCharCode(65 + index); // A, B, C, D...

              return (
                <label
                  key={choice.id || index}
                  className={`
                    flex items-center p-4 my-2 border-2 rounded-xl cursor-pointer transition-all duration-200
                    ${
                      isSelected
                        ? "bg-blue-100 dark:bg-blue-900 border-blue-500 dark:border-blue-400 ring-2 ring-blue-300"
                        : "bg-transparent border-gray-300 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-800"
                    }
                  `}
                >
                  <input
                    type="radio"
                    name={`question-${question.questionId || question.id}`}
                    value={choiceText}
                    onChange={() => onAnswerChange(choiceText)}
                    checked={isSelected}
                    className="mr-3 h-5 w-5 text-blue-600 dark:text-blue-400 focus:ring-blue-500 accent-blue-500 dark:accent-blue-400"
                  />
                  <span className="text-black dark:text-white font-medium">
                    {convertMathJax(choiceText)}
                  </span>
                </label>
              );
            })}
        </div>
      );
    }

    // For True/False questions
    if (
      question.questionType === QuestionType.TRUE_FALSE ||
      question.questionType === "TRUE_FALSE"
    ) {
      const options = ["True", "False"];
      return (
        <div className="mt-4 grid grid-cols-2 gap-4">
          {options.map((option) => {
            const isSelected = answer === option;

            return (
              <button
                key={option}
                onClick={() => onAnswerChange(option)}
                className={`p-6 rounded-xl border-2 transition-all duration-200 font-bold text-lg ${
                  isSelected
                    ? option === "True"
                      ? "border-green-600 bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300"
                      : "border-red-600 bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300"
                    : "border-gray-200 dark:border-gray-700 hover:border-indigo-300 dark:hover:border-indigo-700 bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-300"
                }`}
              >
                <div className="flex flex-col items-center gap-2">
                  <span className="text-4xl">{option === "True" ? "✓" : "✗"}</span>
                  <span>{option}</span>
                </div>
              </button>
            );
          })}
        </div>
      );
    }

    // For short answer / open-ended questions
    if (
      question.questionType === QuestionType.SHORT_ANSWER ||
      question.questionType === "SHORT_ANSWER" ||
      question.questionType === "OPEN_ENDED" ||
      question.questionType === "FILL_IN_THE_BLANK" ||
      question.questionType === "MATHS_CALCULATION"
    ) {
      return (
        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Your Answer:
          </label>
          <textarea
            value={answer || ""}
            onChange={(e) => onAnswerChange(e.target.value)}
            placeholder="Type your answer here..."
            rows={4}
            className="w-full px-4 py-3 border-2 border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:bg-gray-900 dark:text-white resize-none"
          />
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
            Write your answer in the text box above. Be concise and clear.
          </p>
        </div>
      );
    }

    return null;
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-8 mb-6">
      {/* Question Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <span className="text-sm font-bold text-indigo-600 dark:text-indigo-400 bg-indigo-100 dark:bg-indigo-900 px-3 py-1 rounded-full">
            Question {questionNumber} of {totalQuestions}
          </span>
          {question.difficulty && (
            <span
              className={`text-xs font-medium px-3 py-1 rounded-full ${getDifficultyStyles(
                question.difficulty
              )}`}
            >
              {question.difficulty}
            </span>
          )}
        </div>
        {question.subtopic && (
          <span className="text-sm text-gray-600 dark:text-gray-400">
            📚 {question.subtopic}
          </span>
        )}
      </div>

      {/* Question Content - Using existing QuestionRenderer */}
      <div className="p-6 rounded-xl mb-6">
        <QuestionRenderer
          question={normalizedQuestion}
          index={questionNumber - 1}
        />
      </div>

      {/* Answer Choices - Using existing assessment pattern */}
      {renderAnswerChoices()}
    </div>
  );
};

/**
 * Get difficulty badge styles
 */
const getDifficultyStyles = (difficulty) => {
  const styles = {
    BEGINNER: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
    EASY: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
    MEDIUM: "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400",
    HARD: "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400",
    EXPERT: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
  };

  return styles[difficulty] || styles.MEDIUM;
};

export default QuestionDisplay;


